import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { NavTrack, User } from '../lib/types'
import { CATEGORY_LABELS, CATEGORY_BADGE_CLASS, type CategorySlug } from '../lib/types'

const PRIORITY_STYLES = {
  'must-see': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'MUST SEE' },
  'high': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'HIGH' },
  'medium': { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', label: 'MEDIUM' },
}

export default function Tracks({ user }: { user: User | null }) {
  const [tracks, setTracks] = useState<NavTrack[]>([])
  const [progress, setProgress] = useState<Record<string, number>>({})
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.listTracks(),
      user ? api.getTrackProgress().catch(() => []) : Promise.resolve([]),
    ]).then(([t, p]) => {
      setTracks(t)
      const prog: Record<string, number> = {}
      for (const pp of p as any[]) prog[pp.track_id] = pp.current_stop
      setProgress(prog)
    }).finally(() => setLoading(false))
  }, [user])

  async function markVisited(trackId: string, stopOrder: number) {
    const next = progress[trackId] === stopOrder ? stopOrder - 1 : stopOrder
    setProgress(prev => ({ ...prev, [trackId]: next }))
    if (user) api.updateTrackProgress(trackId, next).catch(() => {})
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  const active = selectedTrack ? tracks.find(t => t.id === selectedTrack) : null

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Navigation Tracks</h1>
        <p className="text-gray-600">
          5 curated routes through the show floor. Each prioritizes must-see vendors and estimates time needed.
          Tap a stop to mark it visited.
        </p>
      </div>

      {!active ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tracks.map(track => {
            const visited = progress[track.id] || 0
            const pct = Math.round((visited / track.totalStops) * 100)
            return (
              <button
                key={track.id}
                onClick={() => setSelectedTrack(track.id)}
                className="text-left border-2 rounded-xl p-5 hover:shadow-lg transition-all"
                style={{ borderColor: track.color + '40' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-3xl">{track.icon}</span>
                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: track.color + '20', color: track.color }}>
                    {track.mustSeeCount} must-see
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-1">{track.name}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{track.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <span>{track.totalStops} stops</span>
                  <span>~{track.totalMinutes} min</span>
                </div>
                {visited > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: track.color }} />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      ) : (
        <div>
          <button
            onClick={() => setSelectedTrack(null)}
            className="mb-4 text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
          >
            &larr; All Tracks
          </button>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-4xl">{active.icon}</span>
            <div>
              <h2 className="text-xl font-bold">{active.name}</h2>
              <p className="text-sm text-gray-600">{active.totalStops} stops &middot; ~{active.totalMinutes} min estimated</p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
            <div className="space-y-4">
              {active.stops.map((stop, i) => {
                const visited = (progress[active.id] || 0) >= stop.stop_order
                const style = PRIORITY_STYLES[stop.priority]
                return (
                  <div key={stop.id} className={`relative pl-14 ${visited ? 'opacity-60' : ''}`}>
                    <button
                      onClick={() => markVisited(active.id, stop.stop_order)}
                      className={`absolute left-3.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        visited ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300 hover:border-green-400'
                      }`}
                      style={{ top: '1.25rem' }}
                    >
                      {visited && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>

                    <div className={`border rounded-lg p-4 ${visited ? 'border-green-200 bg-green-50' : `${style.border} ${style.bg}`}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Link
                            to={`/companies/${stop.company_id}`}
                            className="font-bold text-lg hover:text-brand-600 transition-colors"
                          >
                            {stop.company_name}
                          </Link>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-sm font-medium text-gray-600">Booth {stop.booth}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${style.text} ${style.bg}`}>
                              {style.label}
                            </span>
                            <span className="text-xs text-gray-500">~{stop.visit_duration_min} min</span>
                          </div>
                        </div>
                        <span className="text-sm font-mono text-gray-400">#{i + 1}</span>
                      </div>

                      <p className="text-sm text-gray-700 mb-2">{stop.notes}</p>

                      {stop.categories.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {stop.categories.map(cat => (
                            <span
                              key={cat}
                              className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_BADGE_CLASS[cat as CategorySlug] || 'badge-other'}`}
                            >
                              {CATEGORY_LABELS[cat as CategorySlug] || cat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
